import PropTypes from "prop-types";
import React, { Component } from "react";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import {
  Card,
  CardTitle,
  CardText,
  DataTable,
  TableBody,
  TableRow,
  TableColumn,
} from "react-md";
import { cloneDeep } from "lodash";
import {
  normalizeNumberVariableDecimal,
  normalizePhone
} from "components/FormElements";
import { MField, MAction, MSpinner } from "components/Shared";
import * as yup from "yup";
import {
  DATA_TYPE_NUMBER,
  DATA_TYPE_MULTI_LINE_STRING,
  DATA_TYPE_NUMBER_UNFORMATTED,
  DATA_TYPE_PERCENT,
  DATA_TYPE_PERCENT_WHOLE_NUMBER,
  DATA_TYPE_PERCENT_DECIMAL,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_MONEY,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_DATE,
  DATA_TYPE_DATE_TIME,
  DATA_TYPE_TIME,
  DATA_TYPE_DAYS_ELAPSED,
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_EMAIL,
  DATA_TYPE_APPRAISER,
  DATA_TYPE_ADDRESS,
  DATA_TYPE_COUNTY,
  DATA_TYPE_GEOCODE,
  DATA_TYPE_DATE_DOW,
  DATA_TYPE_DATE_TIME_DOW,
  DATA_TYPE_HYPERLINK,
  DATA_TYPE_JSX,
  DATA_TYPE_PASSWORD,
  DATA_TYPE_CREDIT_CARD,
  DATA_TYPE_NUMBER_STRING,
} from "typeConstants/dataTypes";
import {
  booleanToYesNo,
  dateTimeToStringWithNumOfDays,
  moneyFormatter,
  dateToStringYy,
  dateTimeToString,
  dateToStringWithDOW,
  formatPercent,
  formatPercentWholeNumber,
  emailHyperlink,
  mapAddressHyperlink,
  mapCountyHyperlink,
  mapGeocodeHyperlink,
  dateTimeToTime,
  appraiserFormatter,
  dateTimeToStringWithDOW,
  makeHyperlink, 
  checkIfEmpty,
  formatDecimalPercent2,
} from "components/Shared/Formatters";
import { NOT_PROVIDED } from "typeConstants/stringConstants";
import ReactTooltip from "react-tooltip";

class MCard extends Component {
  static contextType = DialogContext;
  constructor(props) {
    super(props);

    this.fieldToRow = this.fieldToRow.bind(this);
    this.onEditClick = this.onEditClick.bind(this);
    this.onApplyClick = this.onApplyClick.bind(this);
    this.getActions = this.getActions.bind(this);
    this.renderTable = this.renderTable.bind(this);
    this.generateEditData = this.generateEditData.bind(this);
    this.getKey = this.getKey.bind(this);
    this.multiLineFormatter = this.multiLineFormatter.bind(this);
    this.exitEditMode = this.exitEditMode.bind(this);
    this.callEditFinished = this.callEditFinished.bind(this);
    this.callEditChanged = this.callEditChanged.bind(this);
    this.runValidation = this.runValidation.bind(this);
    this.getEditedDataObject = this.getEditedDataObject.bind(this);
    this.getClonedEditData = this.getClonedEditData.bind(this);
    this.clearValidationErrors = this.clearValidationErrors.bind(this);
    this.getMdCellClassName = this.getMdCellClassName.bind(this);
    this.mapFieldToEditData = this.mapFieldToEditData.bind(this);
    this.setControlMethods = this.setControlMethods.bind(this);
    this.updateEditData = this.updateEditData.bind(this);
    this.getEditControl = this.getEditControl.bind(this);
    this.validateAfter = this.validateAfter.bind(this);
    this.fieldControlMethods = {};

    this.state = {
      isEditing: props.onlyEditable || props.startInEditMode,
      editData:
        (props.onlyEditable || props.startInEditMode) && !props.isFetching
          ? this.generateEditData()
          : null,
    };
  }

  componentDidMount() {
    if (this.props.getControlMethods)
      this.props.getControlMethods({
        runValidation: validateAll =>
          this.state.isEditing ? this.runValidation(validateAll) : true,
        clearValidationErrors: () => {
          if (this.state.isEditing) this.clearValidationErrors();
        },
        getEditedData: returnAllFields => this.state.isEditing ? this.getEditedDataObject(returnAllFields).editedData : null,
        getEditedDataObject: returnAllFields => this.state.isEditing ? this.getEditedDataObject(returnAllFields).editedDataObjectForm : null,
        setEditData: (key, value, option) => {
          if (!this.state.isEditing) return;
          if (typeof this.fieldControlMethods[key]?.setValue === "function") {
            if (option === "setOption") this.fieldControlMethods[key].setInitialObjectValue(value);
            else {
              this.fieldControlMethods[key].setValue(value);
              this.updateEditData(key, value);
            }
          }
        },
        refreshEditData: () => {
          let currentEditData = this.getClonedEditData();
          let newEditData = this.generateEditData();
          newEditData.forEach(n => {
            if (!currentEditData.some(c => c.key === n.key)) {
              currentEditData.push(n);
            }
          });
          this.setState({ editData: currentEditData });
        }
      });
  }

  componentDidUpdate(prevProps) {
    const { isFetching, onlyEditable, startInEditMode } = this.props;
    if (prevProps.isFetching && !isFetching && (onlyEditable || startInEditMode)) {
      this.setState({ editData: this.generateEditData() });
    }
  }

  renderTable(fields) {
    let rows;

    if (!fields)
      return;

    if (!this.state.isEditing) {
      rows = fields.map(this.fieldToRow);
    } else {
      rows = fields
        .reduce((acc, f) => acc.concat(f.editFields || f), [])
        .map(this.fieldToRow);
    }

    if (this.props.singleColumn) rows = rows.flat(); //fieldToRow returns two rows per field if singleColumn is true

    return (
      <DataTable plain>
        <TableBody>{rows}</TableBody>
      </DataTable>
    );
  }

  getKey(field) {
    return field.key || field.label;
  }

  multiLineFormatter(value) {
    return value
      ? value
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .map((s, i) => <div key={`line_${i}`} style={{whiteSpace: "pre"}}>{s}</div>)
      : "";
  }

  getRawValue(field) {
    switch (field.dataType) {
      case DATA_TYPE_DROPDOWN:
        return field.value && field.value.value;
      default:
        return field.value;
    }
  }

  getRowClassName(field) {
    const { disableHoverStyles } = this.props;
    let classes = [field.className
      ? field.className.endsWith("has-value")
        ? field.value ? `${field.className}-row` : ""
        : `${field.className}-row`
      : ""];
    if (disableHoverStyles)
      classes.push("disable-hover-styles");
    return classes.join(" ");
  }

  setControlMethods(key, controlMethods) {
    this.fieldControlMethods[key] = controlMethods;
  }

  // Need to validate these types after update as the onBlur will not work as expected (or there is no onBlur prop)
  validateAfter(dataType) {
    switch (dataType) {
      case DATA_TYPE_DROPDOWN:
      case DATA_TYPE_DROPDOWN2:
      case DATA_TYPE_BOOLEAN:
      case DATA_TYPE_DATE_TIME:
      case DATA_TYPE_DAYS_ELAPSED:
      case DATA_TYPE_DATE:
      case DATA_TYPE_JSX:
        return true;
      default:
        return false;
    }
  }

  updateEditData(key, newValue) {
    let validateField = {};
    // this setState is being called and the callback is also called, but the changes werent being applied, wrapping in a set timeout to make it async worked.... sometimes ya just gotta
    setTimeout(() => {
      this.setState(state => {
        let field = state.editData.find(e => e.key === key);
        if (field) {
          field.value = newValue;
          field.edited = true;
          validateField = field;
          return { editData: state.editData };
        } return state;
      }, () => {
        if (this.props.onlyEditable) this.callEditFinished();
        this.callEditChanged();
        if (this.validateAfter(validateField.dataType)) this.runValidation();
      });
    }, 0);
  }

  getEditControl(key, field, editData) {
    return (
      <MField
        id={key}
        className={field.className}
        dataType={field.dataType}
        dataTypeOptions={{ ...field.dataTypeOptions }}
        value={editData?.value}
        onChange={(k, v) => { this.updateEditData(k, v); if (field.onChange) field.onChange(v); }}
        validation={field.validation}
        getControlMethods={this.setControlMethods}
        ariaLabel={field.ariaLabel ?? field.label}
        onBlur={() => this.runValidation()}
        placeholder={field.placeholder}
      />
    );
  }

  getValue(field, value) {
    let valueFormatter;
    if (value === undefined) {
      value = this.getRawValue(field);
    }

    switch (field.dataType) {
      case DATA_TYPE_DROPDOWN:
      case DATA_TYPE_DROPDOWN2:
        return field?.value?.label || null;
      case DATA_TYPE_NUMBER:
        valueFormatter = normalizeNumberVariableDecimal(
          field.dataTypeOptions
            ? field.dataTypeOptions.numDigitsAfterDecimal
            : null
        );
        break;
      case DATA_TYPE_NUMBER_UNFORMATTED:
      case DATA_TYPE_NUMBER_STRING:
        valueFormatter = normalizeNumberVariableDecimal(0, true);
        break;
      case DATA_TYPE_MONEY:
        valueFormatter = moneyFormatter(field.dataTypeOptions
          ? field.dataTypeOptions.numDigitsAfterDecimal
          : null);
        break;
      case DATA_TYPE_BOOLEAN:
        valueFormatter = booleanToYesNo;
        break;
      case DATA_TYPE_DATE:
        valueFormatter = dateToStringYy;
        break;
      case DATA_TYPE_DATE_TIME:
        valueFormatter = dateTimeToString;
        break;
      case DATA_TYPE_TIME:
        valueFormatter = dateTimeToTime;
        break;
      case DATA_TYPE_DAYS_ELAPSED:
        valueFormatter = dateTimeToStringWithNumOfDays;
        break;
      case DATA_TYPE_DATE_DOW:
        valueFormatter = dateToStringWithDOW;
        break;
      case DATA_TYPE_DATE_TIME_DOW:
        valueFormatter = dateTimeToStringWithDOW;
        break;
      case DATA_TYPE_PHONE_NUMBER:
        valueFormatter = normalizePhone;
        break;
      case DATA_TYPE_MULTI_LINE_STRING:
        valueFormatter = this.multiLineFormatter;
        break;
      case DATA_TYPE_PERCENT:
        valueFormatter = formatPercent;
        break;
      case DATA_TYPE_PERCENT_WHOLE_NUMBER:
        valueFormatter = formatPercentWholeNumber;
        break;
      case DATA_TYPE_PERCENT_DECIMAL:
        valueFormatter = formatDecimalPercent2(0, true);
        break;
      case DATA_TYPE_EMAIL:
        valueFormatter = emailHyperlink;
        break;
      case DATA_TYPE_PASSWORD:
        valueFormatter = checkIfEmpty;
        break;
      case DATA_TYPE_ADDRESS:
        valueFormatter = mapAddressHyperlink;
        break;
      case DATA_TYPE_COUNTY:
        valueFormatter = mapCountyHyperlink;
        break;
      case DATA_TYPE_GEOCODE:
        valueFormatter = mapGeocodeHyperlink;
        break;
      case DATA_TYPE_APPRAISER:
        valueFormatter = appraiserFormatter;
        break;
      case DATA_TYPE_HYPERLINK:
        value = makeHyperlink(field.value.label, field.value.onClick); // hyperlink: { label, onClick }
        return value;
      case DATA_TYPE_JSX:
        value = field.value;
        return value;
      case DATA_TYPE_CREDIT_CARD:
        value = field.value;
        return value;
    }

    if (valueFormatter) return valueFormatter(value);
    return value === null || value === undefined ? "" : `${value}`; //cast value to string mainly because 0's are falsey and mess up some other logic whereas "0" is not falsey
  }

  getLabelClassName(field) {
    let classes = ["card-field"];
    if (field.className) classes.push(`${field.className}-field`);
    if (field.isLabelOnly) classes.push("card-field-label-only");
    if (field.isIndented) classes.push("card-field-indented");
    return classes.join(" ");
  }

  getValueClassName(field) {
    let classes = [field.className ? (
      `card-value ${field.className}-value`
    ) : (
        "card-value"
      )];

    if (field.conditionalClasses)
      classes = classes.concat(field.conditionalClasses(field));

    return classes.join(" ");
  }

  getErrorClassName(field) {
    return field.className
      ? `card-error ${field.className}-error`
      : "card-error";
  }

  getTooltip(field, tooltipId) {
    return (
      <TableColumn key={`${this.getKey(field)}_tooltip`} className={this.getLabelClassName(field)}>
        <ReactTooltip
          data-tip
          data-for={tooltipId}
          id={tooltipId}
          effect="solid"
          place="right"
          className="mcard-tooltip"
          delayHide={50}
          scrollHide={true}
          border={false}
        >
          <MCard {...field.tooltip} />
        </ReactTooltip>
      </TableColumn>
    );
  }

  fieldToRow(field) {
    const { singleColumn, onlyEditable } = this.props;
    const { isEditing } = this.state;
    const key = this.getKey(field);
    const editData = this.state.editData !== null && this.state.editData.find(ed => ed.key == key);

    if (field.control)
      return (
        <TableRow
          className={this.getRowClassName(field)}
          key={key}
        >
          <TableColumn colSpan={2}>{field.control}</TableColumn>
        </TableRow>
      );

    let value;
    let notProvidedClass = "";
    if ((isEditing && field.editable !== false && field.dataType !== DATA_TYPE_JSX) || onlyEditable) {
      value = this.getEditControl(key, field, editData);
    } else {
      value = this.getValue(field, field.value);
      if (!value || field.value === null) {
        value = NOT_PROVIDED;
        notProvidedClass = "not-provided";
      }
    }

    if (field.isLabelOnly) value = "";
    const isTooltip = !!field.tooltip;
    const tooltipId = isTooltip && `${this.getKey(field)}-tooltip`;

    let lblCol = (
      <TableColumn title={field.tooltipSimple} key={`${this.getKey(field)}_label`} className={this.getLabelClassName(field)} colSpan={field.isLabelOnly ? 2 : 1}>
        {field.label}
      </TableColumn>
    );

    let valCol = field.isLabelOnly ? null : (
      <TableColumn key={`${this.getKey(field)}_value`} className={`${this.getValueClassName(field)} ${notProvidedClass}`}>
        {value}
      </TableColumn>
    );

    let rows;
    if (singleColumn) {
      rows = [
        <TableRow
          data-tip={isTooltip}
          data-for={tooltipId}
          onClick={field.onClick}
          className={this.getRowClassName(field)}
          key={`${this.getKey(field)}_field`}
        >
          <>
            {lblCol}
            {isTooltip && this.getTooltip(field, tooltipId)}
          </>
        </TableRow>,
        <TableRow
          data-tip={isTooltip}
          data-for={tooltipId}
          onClick={field.onClick}
          className={this.getRowClassName(field)}
          key={`${this.getKey(field)}_value`}
        >
          {valCol}
        </TableRow>
      ];
    } else {
      rows = [
        <TableRow
          data-tip={isTooltip}
          data-for={tooltipId}
          onClick={field.onClick}
          className={this.getRowClassName(field)}
          key={this.getKey(field)}
        >
          <>
            {[lblCol, valCol, isTooltip && this.getTooltip(field, tooltipId)]}
          </>
        </TableRow>
      ];
    }

    if (isEditing && editData && editData.error)
      rows.push(
        <TableRow
          className={this.getRowClassName(field)}
          key={`${this.getKey(field)}_error`}
        >
          <TableColumn colSpan={2} className={this.getErrorClassName(field)}>
            {editData.error}
          </TableColumn>
        </TableRow>
      );
    return rows;
  }

  getClonedEditData() {
    return cloneDeep(this.state.editData);
  }

  clearValidationErrors() {
    let editData = this.getClonedEditData();
    editData.forEach(ed => (ed.error = null));
    this.setState({ editData });
  }

  runValidation(validateAllFields) {
    const { fields, startInEditMode } = this.props;
    const editData = this.getClonedEditData();
    let hitCatch = false;

    try {
      yup
        .object(
          fields.reduce((acc, field) => {
            if (field.validation) acc[field.key] = field.validation;
            return acc;
          }, {})
        )
        .validateSync(this.getEditedDataObject(true).editedDataObjectForm, {
          abortEarly: false
        });
    } catch (errors) {
      editData.forEach(ed => {
        let error = errors?.inner?.find(i => ed.key === i.path);

        if (error && (ed.edited || validateAllFields || startInEditMode)) {
          ed.error = error.errors.join(". ");
        } else if (!error) {
          ed.error = null;
        }
      });

      hitCatch = true;
    }

    if (!hitCatch) {
      editData.forEach(ed => (ed.error = null));
    }

    this.setState({ editData });

    return !editData.some(ed => ed.error);
  }

  mapFieldToEditData(field) {
    return {
      key: this.getKey(field),
      value: this.getRawValue(field),
      // Purposely leave previousValue undefined
      originalValue: this.getRawValue(field),
      error: null,
      dataType: field.dataType,
      // If editable is not specified it will default to true
      editable: field.editable === undefined || field.editable,
      edited: false,
      onChange: field.onChange
    };
  }

  generateEditData() {
    return this.props.fields
      ?.flat()
      .reduce(
        (acc, f) => acc.concat((f.editFields || [f]).map(this.mapFieldToEditData)),
        []
      );
  }

  onEditClick() {
    if (this.props.onEditStarted) this.props.onEditStarted();
    this.setState({ isEditing: true, editData: this.generateEditData() });
  }

  getEditedValue(value, dataType) {
    switch (dataType) {
      case DATA_TYPE_DROPDOWN2:
        if (Array.isArray(value)) {
          return value;
        }
        return value?.value;
      default:
        return value;
    }
  }

  getEditedDataObject(returnAllFields) {
    let editedData = this.state.editData
      ?.filter(ed => ed.edited || returnAllFields)
      .map(ed => ({
        key: ed.key,
        value: this.getEditedValue(ed.value, ed.dataType),
        originalValue: ed.originalValue,
        error: ed.error // Error isnt used from this function anywhere in this file but this function is exposed through getEditedData and the user of that may want to know the error
      }))
      // One more filter to get rid of values that ultimately didnt change
      // (user must have edited them and then restored them to their original value)
      // not doing this in the first filter because we dont have the unnormalized value to compare to until after the map
      .filter(ed => ed.value != ed.originalValue || returnAllFields);

    let editedDataObjectForm = editedData?.reduce(
      (acc, ed) => ({ ...acc, [ed.key]: ed.value }),
      {}
    );

    return { editedData, editedDataObjectForm };
  }

  callEditFinished() {
    let ed = this.getEditedDataObject();
    //explicit check for false because we dont want a return of undefined to count as not validated
    return this.props.onEditFinished ? this.props.onEditFinished(ed.editedData, ed.editedDataObjectForm) !== false : true;
  }

  callEditChanged() {
    let ed = this.getEditedDataObject();
    if (this.props.onEditChanged) this.props.onEditChanged(ed.editedData, ed.editedDataObjectForm);
  }

  onApplyClick() {
    let validated = true;

    if (!this.callEditFinished()) validated = false;

    if (validated) this.exitEditMode(false);
  }

  exitEditMode(callEditCancelled) {
    if (this.props.onEditCancelled && callEditCancelled) this.props.onEditCancelled();
    this.setState({
      isEditing: false,
      editData: null,
    });
  }

  getActions() {
    let actions = [];
    if (this.props.isEditable && !this.props.onlyEditable) {
      if (this.state.isEditing) {
        let hasErrors = this.state.editData && this.state.editData.some(ed => ed.error);
        let hasEditedData = this.state.editData?.some(ed => ed.edited);
        actions.push(
          {
            icon: hasErrors ? "playlist_add_check" : "check",
            helpText: hasErrors
              ? "Fix Validation Errors Before Saving"
              : "Save Changes",
            order: 9000,
            ...(!hasErrors && { onClick: this.onApplyClick })
          },
          {
            icon: "close",
            helpText: "Cancel Changes",
            order: 9001,
            onClick: () => hasEditedData ? this.context.showYesNo(null, "You have unsaved changes, are you sure you want to cancel editing?", () => this.exitEditMode(true), () => {}) : this.exitEditMode(true),
          }
        );
      } else {
        actions.push({
          icon: "edit",
          helpText: "Edit",
          order: 9002,
          onClick: this.onEditClick
        });
      }
    }
    if (this.props.actions && this.props.actions.length > 0) {
      this.props.actions.forEach((action, index) => {
        actions.push({
          key: index,
          ...action,
        });
      });
    }
    if (actions.length) {
      actions.sort((a, b) => a.order > b.order ? 1 : -1);
      return (
        <div className="mcard-edit-actions">{
          actions.map((action, index) =>
            <MAction
              key={`action_${index}`}
              {...action}
            />)
        }</div>
      );
    }
  }

  getMdCellClassName() {
    return this.props.cellWidth ? ` md-cell md-cell--${this.props.cellWidth}` : "";
  }

  render() {
    const { title, headerContent, isFetching, children, className } = this.props;

    let fields;
    if (this.props.fields && !Array.isArray(this.props.fields[0])) {
      if (this.props.allowMultipleColumns) {
        let sliceIndex = Math.ceil(this.props.fields.length / 2);
        fields = [[...this.props.fields.slice(0, sliceIndex)], [...this.props.fields.slice(sliceIndex)]];
      } else {
        fields = [this.props.fields];
      }
    } else {
      fields = this.props.fields;
    }

    let divStyle = fields && { width: `${(1 / fields.length) * 100}%` };
    return (
      <Card className={"mcard card-unnested " + (className || "") + this.getMdCellClassName()}>
        <CardTitle title="" className="mcard-title">
          <div className="mcard-title-text" title={this.props.toolTipTitle}>{title}</div>
          {headerContent}
          {this.getActions()}
        </CardTitle>
        {isFetching && <MSpinner />}
        {this.props.fields && !isFetching && <CardText className="mcard-fields-section">
          {fields.map((f, i) => (
            <div key={`table_${i}`} style={divStyle}>
              {this.renderTable(f)}
            </div>
          ))}
        </CardText>}
        {!isFetching && children && <CardText>{children}</CardText>}
      </Card>
    );
  }
}

MCard.defaultProps = {
  tooltip: null,
  disableHoverStyles: false,
  startInEditMode: false,
  headerContent: null,
};

MCard.propTypes = {
  title: PropTypes.string.isRequired, // Card title
  className: PropTypes.string, // Class attached to the card
  cellWidth: PropTypes.number, // Cell width size if you want the card to be a part of an md-cell
  style: PropTypes.string, // Style for when you need to do math for dynamic styling
  /* fields expects:
    [{
      // Note: validation only looks at controls that have a key property provided in the fields
      key, optional - (required if using control field or if you need validation) used for the unique react key for the collection of objects rendered to the screen. if not provided label will be used for a key. if using the control field you need to provide a key because you wont be providing a label, also used for the data return if the card is editable
      control, optional - if provided, nothing else below matters. the card will just render the control provided here and it will be allowed to span both columns
      label, optional - not needed if control is specified
      className, optional - will be rendered onto the columns as className-field and className-value, the row will also get the style className-row
      // Accepts dates, strings, numbers, and booleans
      // If using DATA_TYPE_DROPDOWN pass { label, value }
      // If using  pass { label, onClick } onClick can be a function or a string, if its a string its just the address to navigate to
      value,
      dataType, one of the exported DATA_TYPE_... defines how the card data is formatted and how it is edited
      dataTypeOptions: { not required, options for certain data types
        numDigitsAfterDecimal, if not specified any amount is allowed, only used for DATA_TYPE_NUMBER
        options, array of { label, value } or just array of strings (labels) to display in a drop down edit list if the dataType is DATA_TYPE_DROPDOWN, value will be returned unless it is undefined, then label will be returned
        nullable, true by default, if false the clear value icon will not show for DATA_TYPE_DATE, DATA_TYPE_DATE_TIME, DATA_TYPE_DAYS_ELAPSED, DATA_TYPE_DROPDOWN
        minDate, maxDate: by default not set, instanceOf(Date), if DATA_TYPE_DATE, DATA_TYPE_DATE_TIME, DATA_TYPE_DAYS_ELAPSED min and max dates will set the boundaries for the day selection from datepicker
        sortFunc, if using DATA_TYPE_DROPDOWN2 this is the sortFunc that will get passed to MSelect to sort the dropdown
        isMultiSelect, if using DATA_TYPE_DROPDOWN2, it makes it a multi select,
        isShortMulti, if using DATA_TYPE_DROPDOWN2, set this to true to show a select all option and not display the selected items in the field after blur,
        streetAddressOnly, if using DATA_TYPE_ADDRESS_AUTOCOMPLETE, set this to true to show only the street address. Otherwise the whole address will be shown,
        creatable, if using DATA_TYPE_DROPDOWN2, this will allow the user to add an item to the drop down
        Note: if using creatable, your values in the options must be strings as the component calls .toLowerCase on the value
      },
      // Note: yup.when() does not appear to work with the current implementation of MCard
      validation, optional - yup validation line for this field, ex: yup.number("Field must be a number").positive("Field must be positive").integer("Field must be an integer") (with: import * as yup from "yup")
      isLabelOnly, optional - if true then no value will be displayed and the cell will get a className of "card-field-label-only", will appear "bold"
        if you want to use a label to display validation, give it the className "card-error" and it will appear the same as validation messages
      isIndented, optional - if true then will get a className of "card-field-indented", the field will shift 30px to the left
      tooltip, optional - set of MCard props to render an MCard tooltip on field hover
      tooltipSimple, optional - string that represents a tooltip just for the label of the card row, not the value
      editable, optional - by default cards are not editable but the isEditable prop is true then all fields are editable by default. DATA_TYPE_HYPERLINK is not editable.
      editFields, optional - array of fields (no support for controls) that show on edit instead of this field, ex: field is Name - First + Last, edit fields are First Name, Last Name, this allows us to display two fields as one but edit them separately
      onClick, - optional - callback function when field is clicked
      onChange, - optional - callback when value is changed while editing
    }, ...] */
  fields: PropTypes.array,
  children: PropTypes.node, // Specifying children and not fields will just put the child control inside of the card
  isEditable: PropTypes.bool, // Default false, set true if you want the card to be editable
  /* onlyEditable forces the card to be only editable
     there are no icons to enter edit mode or cancel edit mode
     IMPORTANT NOTE: in only editable mode, onEditFinished will be called every time something is changed
                     please do not do database calls in onEditFinished in this case */
  onlyEditable: PropTypes.bool,
  onEditStarted: PropTypes.func, // Callback that gets called when edit is clicked
  onEditCancelled: PropTypes.func, // Callback that gets called when cancel edit is clicked
  /* callback called when a card is edited and apply is selected
     Called with two parameters
     1st param: [{ key, value, originalValue }, ...]
     2nd param: { key1: value1, key2: value2, ...}
     only the keys that have been edited will be provided
     if keys were not provided, the labels will be provided as a key instead */
  onEditFinished: PropTypes.func,
  onEditChanged: PropTypes.func, // Called with the same params as onEditFinished, but is called every time a field is changed
  singleColumn: PropTypes.bool, // Use this if you want the values to display below the fields
  headerContent: PropTypes.node, // Use this if you want a custom element in the MCard Header (left of actions) //Any React Node
  actions: PropTypes.array, // Use this if you want custom icons in the MGrid Header //{code, icon, buttonText, helpText, onClick, order, disabled}
  startInEditMode: PropTypes.bool, // Use this to start the card in edit mode instead of readonly
  /* this callback function will be called with an object that has multiple functions on it that let you control MCard
  {
    runValidation(validateAllFields), // Runs validation, show errors on the form, and return true if validated, false otherwise. validateAllFields defaults to false, only fields that have been edited will be validated by default
    clearValidationErrors(), // Clears all validation errors
    getEditedData(), // Returns an array of objects for each edited field with the following:
    // { key, value, originalValue, error }
    getEditedData(),
    getEditedDataObject(), // Returns a single object with key value pairs for each edited field
    setEditData(key, value),  // Calls MField control method to set the value for the given key
    refreshEditData(),  // Updates editData without destroying existing data
  }
  */
  getControlMethods: PropTypes.func,
  disableHoverStyles: PropTypes.bool,
  isFetching: PropTypes.bool, //show an MSpinner is we are loading
  toolTipTitle: PropTypes.string, // show hover text on MCard title
  allowMultipleColumns: PropTypes.bool, // allows multiple columns
};

export default MCard;
